/**
 * Component: AIImageAddonCard
 * Author: Dine AI
 * Last Modified: December 17, 2025 - Light mode conversion
 */
import { motion } from 'framer-motion';
import { Camera, TrendingUp, ArrowRight, Star, ChefHat, Eye } from 'lucide-react';
import { useState } from 'react';
import { apiClient } from '@/services/api/apiClient';
import './AIImageAddonCard.css';

interface AIImageAddonCardProps {
  plan: string;
  imageUsage?: {
    used: number;
    limit: number;
    remaining: number;
  };
  onPurchaseComplete?: () => void;
}

const AIImageAddonCard = ({ plan, imageUsage, onPurchaseComplete }: AIImageAddonCardProps) => {
  const [processingCheckout, setProcessingCheckout] = useState(false);

  const handlePurchase = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('🚀 AI Image Add-on: Purchase button clicked');
    setProcessingCheckout(true);
    try {
      console.log('🔄 Making API call to /stripe/ai-addon-checkout');
      
      // Create checkout session - backend handles URLs like subscription flow
      const payload = {
        product_key: 'ai_images_100'
      };
      
      console.log('📦 Payload:', JSON.stringify(payload, null, 2));
      
      const response = await apiClient.post('/stripe/ai-addon-checkout', payload);

      console.log('✅ API Response:', response);
      
      // Handle both possible response structures
      const data = response.data || response;
      
      if (data.success && data.checkout_url) {
        console.log('🔗 Redirecting to Stripe:', data.checkout_url);
        // Call the callback if provided (though we'll redirect anyway)
        if (onPurchaseComplete) {
          onPurchaseComplete();
        }
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else if (data.checkout_url) {
        // Sometimes success flag might be missing but URL exists
        console.log('🔗 Redirecting to Stripe (no success flag):', data.checkout_url);
        if (onPurchaseComplete) {
          onPurchaseComplete();
        }
        window.location.href = data.checkout_url;
      } else {
        console.error('❌ No checkout URL in response:', response);
        alert('Failed to create checkout session. Please try again.');
      }
    } catch (error: any) {
      console.error('❌ Purchase error:', error);
      
      // Check if it's an authentication error
      if (error.response?.status === 401) {
        console.log('🔐 Authentication error - redirecting to login');
        window.location.href = '/login';
      } else {
        alert(`Failed to process purchase: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
      }
    } finally {
      setProcessingCheckout(false);
    }
  };

  // High-quality sample image from The Flavor Lab
  const sampleImage = '/images/60/menu/4245/v2.png'; // Seared Scallops

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6 }}
      className="relative overflow-hidden group"
    >
      {/* Main Card - Light Mode */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm group-hover:border-gray-300 transition-all overflow-hidden">
        {/* Content Layout: Image Left (Full Height), Content Right */}
        <div className="flex flex-col lg:flex-row h-full">
          {/* Left Side - Full Height Food Image */}
          <div className="lg:w-2/5 relative">
            <div className="relative w-full h-56 lg:h-full min-h-[320px]">
              <img 
                src={sampleImage} 
                alt="Exquisite Seared Scallops"
                className="absolute inset-0 w-full h-full object-cover food-image-enhanced"
                onError={(e) => {
                  // Fallback to a placeholder if image fails
                  e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iIzJhNGU4NCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmaWxsPSIjZmZmIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiPkRlbGljaW91cyBGb29kPC90ZXh0Pjwvc3ZnPg==';
                }}
              />
              {/* Gradient Overlay for Better Text Visibility */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/20" />
              
              {/* AI Generated Badge */}
              <div className="absolute top-3 left-3">
                <div className="px-3 py-1.5 bg-black/50 backdrop-blur-md rounded-full flex items-center gap-1.5">
                  <Camera className="w-3.5 h-3.5 text-purple-400" />
                  <span className="text-xs font-medium text-white">AI Generated</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Sales Content */}
          <div className="lg:w-3/5 p-6 lg:p-7 flex flex-col justify-between">
            {/* Compelling Headline */}
            <div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2 leading-tight">
                Beautiful Food Photos Increase Customer Appetite & Orders
              </h3>

              {/* Value Proposition */}
              <p className="text-gray-500 mb-5 text-sm lg:text-base">
                Transform your menu with stunning AI-generated food photography that makes dishes irresistible
              </p>

              {/* Key Benefits with Icons */}
              <div className="space-y-2.5 mb-5">
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-green-100 rounded-lg mt-0.5">
                    <TrendingUp className="w-3.5 h-3.5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">73% More Orders</p>
                    <p className="text-gray-500 text-xs">Menu items with photos get ordered significantly more</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-blue-100 rounded-lg mt-0.5">
                    <Eye className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Instant Visual Appeal</p>
                    <p className="text-gray-500 text-xs">Professional quality images in seconds, not hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-purple-100 rounded-lg mt-0.5">
                    <ChefHat className="w-3.5 h-3.5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Perfect Food Styling</p>
                    <p className="text-gray-500 text-xs">AI ensures every dish looks its absolute best</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Pricing and CTA Section */}
            <div>

              {/* Price and Purchase */}
              <div className="flex items-end justify-between mb-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">100 Professional Images</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-gray-900">$9.99</span>
                    <span className="text-base text-gray-400 line-through">$19.99</span>
                    <span className="px-2.5 py-1 bg-green-100 text-green-600 text-sm font-bold rounded-full badge-pulse">
                      50% OFF
                    </span>
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <button
                type="button"
                onClick={handlePurchase}
                disabled={processingCheckout}
                className="w-full py-3 bg-[#FDB913] hover:bg-[#e5a711] text-black rounded-lg font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5"
              >
                {processingCheckout ? (
                  <>Processing...</>
                ) : (
                  <>
                    Get Instant Access
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              {/* Trust Indicators */}
              <p className="text-xs text-gray-400 text-center mt-2.5">
                One-time purchase • No subscription • Instant delivery
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AIImageAddonCard;